/**
 * carriers-api.ts — typed feature wrapper + wire→view-model mapping for the
 * Versicherer list (Story V10-J2).
 *
 * Live wire source: GET /carriers (CarrierListItemDto — policy aggregates per
 * carrier_uid ⋈ company_master name). Per V10-J AC3 precedent the mapping
 * lives HERE in the client layer, not in the components.
 *
 * Mapping decisions (display-only; no domain rules recomputed client-side):
 *   - id: carrier_uid (the register UID is the row identity + route param).
 *   - name: register name, fallback = the uid (no register row yet).
 *   - volumen: premium_sum numeric-string → number (0 when null/denied).
 *   - nextRenewal: next_expiry ISO date → 'in N Tagen' display (— when none).
 *
 * [Source: apps/web/src/features/customers/api/customers-api.ts] (wrapper pattern)
 * [Source: docs/stories/v10-j2-web-wiring-rest.md AC4]
 */

import { apiClient } from '../../../lib/api-client';
import { daysUntil } from '../../../lib/format';
import type { CarrierListItemDto } from '@brokly/shared';
import type { Carrier } from '../../entity-detail/types';

/** ISO date → the 'in N Tagen' display the Carrier view renders ('—' when none). */
export function nextRenewalLabel(nextExpiry: string | null): string {
  const days = daysUntil(nextExpiry);
  if (days === null) return '-';
  if (days <= 0) return 'heute';
  if (days === 1) return 'in 1 Tag';
  return `in ${days} Tagen`;
}

/** One wire row → the Carrier view-model the Versicherer list renders. */
export function mapCarrierToRow(dto: CarrierListItemDto): Carrier {
  return {
    id: dto.carrier_uid,
    name: dto.name ?? dto.carrier_uid,
    policen: dto.policy_count,
    volumen: dto.premium_sum !== null ? Number(dto.premium_sum) : 0,
    nextRenewal: nextRenewalLabel(dto.next_expiry),
    emailSubmission: dto.email_submission,
    logoUrl: dto.logo_url,
    formTemplateRef: dto.form_template_ref,
    emailSubmissionOwn: dto.email_submission_own,
    emailSubmissionGlobal: dto.email_submission_global,
    emailSubmissionSource: dto.email_submission_source,
    formTemplateRefOwn: dto.form_template_ref_own,
    formTemplateRefGlobal: dto.form_template_ref_global,
    formTemplateRefSource: dto.form_template_ref_source,
    // P2 hardening 02 — the Versicherer entity fields.
    status: dto.status,
    city: dto.city,
    canton: dto.canton,
    zefixStatus: dto.zefix_status,
    origin: dto.origin,
    versichererId: dto.id,
  };
}

/** Fetch + map the Versicherer rows (GET /carriers). */
export async function fetchCarrierRows(): Promise<Carrier[]> {
  const response = await apiClient.carriers.list();
  return response.items.map(mapCarrierToRow);
}
